import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "config";
import { USERID_CLAIMS } from "system-constants";

type WebsocketConnection = {
  UserId: string;
  EncounterId: string;
  ChartCode: string;
  SectionCode: string;
  UserName?: string;
  UserColor?: string;
};

type EditingStateMessage = {
  type: "editingState";
  orderId: string;
  isEditing: boolean;
};

type DataChangeMessage = {
  encounterId: string;
  sectionCode: string;
  changeType: string;
  changedByUserId: string;
  changedByUserName: string;
  timestamp: number;
};

type WebsocketResponse = {
  MessageType: string;
  EditingState?: {
    OrderId: string;
    IsEditing: boolean;
    Granted: boolean;
    UserId: string;
    UserName: string;
    UserColor: string;
  };
  UserPresence?: {
    UserId: string;
    UserName: string;
    UserColor: string;
    IsConnected: boolean;
    ConnectedUsers: Array<{
      UserId: string;
      UserName: string;
      UserColor: string;
      IsConnected: boolean;
    }>;
  };
  DataChange?: DataChangeMessage;
};

type EditingLock = {
  userId: string;
  userName: string;
  userColor: string;
};

type ConnectedUser = {
  userId: string;
  userName: string;
  userColor: string;
};

type ConnectionStatus = "disconnected" | "connecting" | "connected";

type UseRealtimeIvMedicationsOptions = {
  encounterId: string;
  chartCode: string;
  sectionCode: string;
  onDataChange?: (change: DataChangeMessage) => void;
  onReconnect?: () => void;
};

function useRealtimeIvMedications({
  encounterId,
  chartCode,
  sectionCode,
  onDataChange,
  onReconnect,
}: UseRealtimeIvMedicationsOptions) {
  const { user, getAccessTokenSilently } = useAuth0();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const disposedRef = useRef(false);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [editingLocks, setEditingLocks] = useState<Record<string, EditingLock>>(
    {}
  );
  const currentUserIdRef = useRef<string>("");

  const isConnectingRef = useRef<boolean>(false);
  const userRef = useRef(user);
  const onDataChangeRef = useRef(onDataChange);
  const onReconnectRef = useRef(onReconnect);
  const wasConnectedRef = useRef(false);
  const retryCountRef = useRef<number>(0);
  const hasReceivedPresenceRef = useRef(false);
  const presenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentlyEditingOrderIdsRef = useRef<Set<string>>(new Set());

  const _MAX_RETRIES = Infinity;
  const BASE_RETRY_DELAY = 1;

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  const sendMessage = useCallback((message: unknown) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const messageStr = JSON.stringify(message);
      socketRef.current.send(messageStr);
    }
  }, []);

  const sendEditingState = useCallback(
    (orderId: string, isEditing: boolean) => {
      if (isEditing) {
        currentlyEditingOrderIdsRef.current.add(orderId);
      } else {
        currentlyEditingOrderIdsRef.current.delete(orderId);
      }
      const message: EditingStateMessage = {
        type: "editingState",
        orderId,
        isEditing,
      };
      sendMessage(message);
    },
    [sendMessage]
  );

  const handleWebSocketMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const response: WebsocketResponse = JSON.parse(event.data);

        // Handle keepalive ping from server — respond with pong
        if (
          response.MessageType === "ping" ||
          (response as { type?: string }).type === "ping"
        ) {
          sendMessage({ type: "pong", timestamp: Date.now() });
          return;
        }

        switch (response.MessageType) {
          case "editingState":
            if (response.EditingState) {
              const {
                UserId,
                UserName,
                UserColor,
                OrderId,
                IsEditing,
                Granted,
              } = response.EditingState;

              // Ignore own confirmations
              if (UserId === currentUserIdRef.current) {
                break;
              }

              setEditingLocks((prev) => {
                if (
                  Granted &&
                  IsEditing &&
                  UserId !== currentUserIdRef.current
                ) {
                  return {
                    ...prev,
                    [OrderId]: {
                      userId: UserId,
                      userName: UserName,
                      userColor: UserColor,
                    },
                  };
                } else if (Granted && !IsEditing) {
                  const updated = { ...prev };
                  delete updated[OrderId];
                  return updated;
                } else if (!Granted) {
                  return {
                    ...prev,
                    [OrderId]: {
                      userId: UserId,
                      userName: UserName,
                      userColor: UserColor,
                    },
                  };
                }
                return prev;
              });
            }
            break;

          case "userPresence":
            if (response.UserPresence) {
              const { ConnectedUsers } = response.UserPresence;
              setConnectedUsers(
                ConnectedUsers.map((u) => ({
                  userId: u.UserId,
                  userName: u.UserName,
                  userColor: u.UserColor,
                }))
              );
              if (!hasReceivedPresenceRef.current) {
                hasReceivedPresenceRef.current = true;
                setConnectionStatus("connected");
                if (presenceTimeoutRef.current) {
                  clearTimeout(presenceTimeoutRef.current);
                  presenceTimeoutRef.current = null;
                }
              }
            }
            break;

          case "dataChange":
            if (response.DataChange) {
              onDataChangeRef.current?.(response.DataChange);
            }
            break;
        }
      } catch {
        // silently fail to avoid disrupting UX
      }
    },
    [sendMessage]
  );

  const connectWebSocket = useCallback(async () => {
    if (
      isConnectingRef.current ||
      socketRef.current?.readyState === WebSocket.OPEN ||
      socketRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    setConnectionStatus("connecting");
    isConnectingRef.current = true;

    try {
      const token = await getAccessTokenSilently({ ignoreCache: false });

      if (disposedRef.current) {
        return;
      }

      const currentUser = userRef.current;
      const userId = currentUser ? currentUser[USERID_CLAIMS] : "";
      const userName = currentUser?.name || currentUser?.email || userId;

      if (!userId) {
        isConnectingRef.current = false;
        return;
      }

      currentUserIdRef.current = userId;

      const identityApiBaseUrl = config.identityApiBaseUrl
        ?.replace("https://", "wss://")
        .replace("http://", "ws://");

      if (!identityApiBaseUrl) {
        isConnectingRef.current = false;
        return;
      }

      const existingSocket = socketRef.current;
      if (existingSocket) {
        existingSocket.onopen = null;
        existingSocket.onmessage = null;
        existingSocket.onclose = null;
        existingSocket.onerror = null;
        if (
          existingSocket.readyState === WebSocket.OPEN ||
          existingSocket.readyState === WebSocket.CONNECTING
        ) {
          existingSocket.close();
        }
      }

      socketRef.current = new WebSocket(
        `${identityApiBaseUrl}${
          identityApiBaseUrl.endsWith("/") ? "" : "/"
        }ws?access_token=${token}`
      );

      const ws = socketRef.current;

      ws.onopen = () => {
        if (disposedRef.current) {
          return;
        }

        if (!ws || ws.readyState !== WebSocket.OPEN) {
          return;
        }

        isConnectingRef.current = false;
        retryCountRef.current = 0;

        // Set presence fallback BEFORE send/reconnect.
        // If send() throws (e.g. socket closing during server restart),
        // the 5s timeout still fires and UI recovers from "connecting".
        presenceTimeoutRef.current = setTimeout(() => {
          if (!hasReceivedPresenceRef.current && !disposedRef.current) {
            setConnectionStatus("connected");
          }
        }, 5000);

        const connection: WebsocketConnection = {
          UserId: userId,
          EncounterId: encounterId,
          ChartCode: chartCode,
          SectionCode: sectionCode,
          UserName: userName,
        };

        try {
          ws.send(JSON.stringify(connection));
        } catch {
          // send() can throw if the socket transitions to CLOSING between
          // the readyState check and the send (e.g. during server restart).
          // The presence timeout above still fires after 5s, so the UI recovers.
        }

        if (wasConnectedRef.current) {
          try {
            onReconnectRef.current?.();
          } catch {
            // ignore errors from the reconnect callback
          }
        }
        wasConnectedRef.current = true;

        currentlyEditingOrderIdsRef.current.forEach((orderId) => {
          sendEditingState(orderId, true);
        });
      };

      socketRef.current.onmessage = handleWebSocketMessage;

      socketRef.current.onclose = () => {
        if (disposedRef.current) {
          return;
        }

        hasReceivedPresenceRef.current = false;
        isConnectingRef.current = false;

        if (presenceTimeoutRef.current) {
          clearTimeout(presenceTimeoutRef.current);
          presenceTimeoutRef.current = null;
        }

        setConnectionStatus("connecting");
        setConnectedUsers([]);
        setEditingLocks({});

        const delay =
          Math.min(
            BASE_RETRY_DELAY * Math.pow(1.5, retryCountRef.current),
            30
          ) *
          (0.5 + Math.random() * 0.5);
        retryCountRef.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connectWebSocket();
        }, delay * 1000) as unknown as NodeJS.Timeout;
      };

      socketRef.current.onerror = () => {
        if (disposedRef.current) {
          return;
        }

        isConnectingRef.current = false;
      };
    } catch {
      if (!disposedRef.current) {
        hasReceivedPresenceRef.current = false;
        isConnectingRef.current = false;

        if (presenceTimeoutRef.current) {
          clearTimeout(presenceTimeoutRef.current);
          presenceTimeoutRef.current = null;
        }

        setConnectionStatus("connecting");

        const delay =
          Math.min(
            BASE_RETRY_DELAY * Math.pow(1.5, retryCountRef.current),
            30
          ) *
          (0.5 + Math.random() * 0.5);
        retryCountRef.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connectWebSocket();
        }, delay * 1000) as unknown as NodeJS.Timeout;
      }
    }
  }, [
    getAccessTokenSilently,
    handleWebSocketMessage,
    sendEditingState,
    encounterId,
    chartCode,
    sectionCode,
  ]);

  const cleanupSocket = useCallback(() => {
    disposedRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (presenceTimeoutRef.current) {
      clearTimeout(presenceTimeoutRef.current);
      presenceTimeoutRef.current = null;
    }

    const socket = socketRef.current;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
      socketRef.current = null;
    }

    isConnectingRef.current = false;
    wasConnectedRef.current = false;
    retryCountRef.current = 0;
    hasReceivedPresenceRef.current = false;
    setConnectionStatus("disconnected");
    setConnectedUsers([]);
    setEditingLocks({});
  }, []);

  useEffect(() => {
    disposedRef.current = false;

    if (
      socketRef.current?.readyState === WebSocket.OPEN ||
      socketRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return cleanupSocket;
    }

    connectWebSocket();

    return cleanupSocket;
  }, [connectWebSocket, cleanupSocket]);

  return {
    connectionStatus,
    connectedUsers,
    editingLocks,
    sendEditingState,
  };
}

export { useRealtimeIvMedications };
export type { ConnectedUser, DataChangeMessage, ConnectionStatus, EditingLock };
